'use client'

import { useMutation, useQuery } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'

import {
  createCheckoutSession,
  fetchPlans,
  fetchSubscription,
  openBillingPortal,
} from '@/service/billing.api'
import { authAtom } from '@/state/auth'
import {
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  OpenBillingPortalRequest,
  OpenBillingPortalResponse,
} from '@/types/generated/public_api'
import { ErrorResponse } from '@/types/service'

export const billingKeys = {
  plans: ['billing', 'plans'] as const,
  subscription: (userId?: string | null) => ['billing', 'subscription', userId] as const,
}

/**
 * Public plan catalog (`get_plans`). Unauthenticated and stable, so it's cached
 * generously and shared by the pricing page and the comparison table.
 */
export function usePlans({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: billingKeys.plans,
    queryFn: fetchPlans,
    staleTime: 5 * 60 * 1000,
    enabled,
  })
}

/**
 * The caller's effective subscription + capabilities (`get_subscription`).
 * Re-fetches on window focus so the badge and feature gates can't go stale after
 * the user returns from Stripe Checkout or the Billing Portal. Gated on auth.
 */
export function useSubscription() {
  // Read auth state straight from the atom — useCapabilities (and thus this hook)
  // is mounted in many hot widgets, and useAuth() would re-run its cookie-init
  // effect / instantiate router+passkey hooks per widget for no benefit here.
  const { isAuthenticated, user } = useAtomValue(authAtom)
  return useQuery({
    queryKey: billingKeys.subscription(user?.userId),
    queryFn: fetchSubscription,
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
    // Re-check on focus so the badge/gates refresh after the user returns from
    // Stripe; the 30s staleTime suppresses redundant refetches while they work.
    refetchOnWindowFocus: true,
  })
}

export function useCreateCheckoutSession(props?: {
  onSuccess?: (data: CreateCheckoutSessionResponse) => void
  onError?: (error: ErrorResponse) => void
}) {
  return useMutation({
    mutationFn: (payload: CreateCheckoutSessionRequest) => createCheckoutSession(payload),
    onSuccess: props?.onSuccess,
    onError: props?.onError,
  })
}

export function useOpenBillingPortal(props?: {
  onSuccess?: (data: OpenBillingPortalResponse) => void
  onError?: (error: ErrorResponse) => void
}) {
  return useMutation({
    mutationFn: (payload: OpenBillingPortalRequest) => openBillingPortal(payload),
    onSuccess: props?.onSuccess,
    onError: props?.onError,
  })
}
